import { useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import Loader from 'components/Loader';

// Google Charts drops an annotation back inside the bar when there is no room above it, which
// happens whenever the tallest bar reaches the top of the chart area. Pad the value axis so every
// bar, including the tallest, has space for its label.
function withHeadroom(chartOptions, rows) {
  if (!Array.isArray(rows) || rows.length < 2) return chartOptions;
  const header = rows[0];
  const isValue = (i) => i > 0 && !(header[i] && header[i].role);
  let max = 0;
  rows.slice(1).forEach((row) => {
    const values = row
      .map((v, i) => (isValue(i) && typeof v === 'number' ? v : 0))
      .slice(1);
    const rowMax = chartOptions.isStacked
      ? values.reduce((a, b) => a + b, 0)
      : Math.max(0, ...values);
    if (rowMax > max) max = rowMax;
  });
  if (!max) return chartOptions;
  return {
    ...chartOptions,
    vAxis: {
      ...(chartOptions.vAxis || {}),
      viewWindow: {
        min: 0,
        max: max * 1.15,
      },
    },
  };
}

// Google Charts rejects an empty table with a red "Table has no columns" box. Until a report has
// been fetched, or when the period has nothing for a chart, show a quiet placeholder instead.
function ReportChart({ data, ...props }) {
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-gray-500">
        No data for the selected filters.
      </div>
    );
  }
  return <Chart data={data} {...props} />;
}

export function MonthlyCharts({ results, loading }) {
  const [data, setData] = useState([]);
  const [datac, setDataC] = useState([]);
  const [radioc, setRadioC] = useState([]);
  const [artistData, setArtistData] = useState([]);
  const [artistRadio, setArtistRadio] = useState([]);
  const [intArtistData, setIntArtistData] = useState([]);
  const [natArtistData, setNatArtistData] = useState([]);
  const [songData, setSongData] = useState([]);
  const [songRadio, setSongRadio] = useState([]);
  const [recogData, setRecogData] = useState([]);
  const [recogRadio, setRecogRadio] = useState([]);
  const [recogDataC, setRecogDataC] = useState([]);
  const [recogRadioC, setRecogRadioC] = useState([]);
  const [recogDuration, setRecogDuration] = useState([]);
  const [recogDurationRadio, setRecogDurationRadio] = useState([]);
  const IntOptions = {
    title: '',
    legend: { position: 'bottom' },
    chartArea: { width: '90%' },
    annotations: { alwaysOutside: true },
    isStacked: false,
    hAxis: {
      title: '',
      minValue: 0,
    },
    series: {
      0: { color: '#2980b9', axis: 'National' },
      1: { color: '#e74c3c', axis: 'International' },
    },
    axes: {
      y: {
        National: { label: 'parsecs' },
        International: { side: 'right', label: 'apparent magnitude' },
      },
    },
    vAxis: {
      title: '',
      textPosition: 'none',
    },
  };

  const options = {
    title: '',
    legend: { position: 'bottom' },
    chartArea: { width: '90%' },
    annotations: { alwaysOutside: true },
    isStacked: true,
    hAxis: {
      title: '',
      minValue: 0,
    },
    vAxis: {
      title: '',
      textPosition: 'none',
    },
    series: {
      0: { color: '#2980b9' },
    },
  };

  useEffect(() => {
    function percentage(top, bottom) {
      // Either side can be null when a station has nothing in that bucket for the period; the old
      // version turned that into "NaN" on the bar.
      const total = (top ?? 0) + (bottom ?? 0);
      if (!total) return '0.00';
      return (((top ?? 0) / total) * 100).toFixed(2);
    }

    if (
      results?.catalogue_data !== null &&
      results?.catalogue_data !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'National',
        { role: 'annotation' },
        'International',
        { role: 'annotation' },
      ]);
      results?.catalogue_data?.map((res) => {
        // The bar has to be the same measure as the label. This chart is titled Playtime(%), so both
        // are the share of playtime. It used to plot res.national - a count of plays - while printing
        // the duration percentage on top of it, and the two disagree: a station can play more
        // international tracks than national ones and still spend more of its airtime on national,
        // because international detections are much shorter on average.
        const national = percentage(res.national_duration, res.international_duration);
        const international = percentage(
          res.international_duration,
          res.national_duration,
        );
        result.push([
          res.broadcast_stream__name,
          Number(national),
          national,
          Number(international),
          international,
        ]);
      });
      setData(result);
    }

    if (
      results?.catalogue_data_count !== null &&
      results?.catalogue_data_count !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'National',
        { role: 'annotation' },
        'International',
        { role: 'annotation' },
      ]);
      // Reads catalogue_data_count, not catalogue_data: these are distinct songs, which is what the
      // backend computes for this chart and what "count" means in the reports. Mapping catalogue_data
      // here repeated the play counts and threw catalogue_data_count away.
      results?.catalogue_data_count?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          res.national,
          res.national,
          res.international,
          res.international,
        ]);
      });
      setDataC(result);
    }

    if (
      results?.catalogue_data_count_radio !== null &&
      results?.catalogue_data_count_radio !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'National',
        { role: 'annotation' },
        'International',
        { role: 'annotation' },
      ]);
      results?.catalogue_data_count_radio?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          res.national,
          res.national,
          res.international,
          res.international,
        ]);
      });
      setRadioC(result);
    }

    if (results?.recognition !== null && results?.recognition !== undefined) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          Number(percentage(res.recognised, res.unrecognised)),
          percentage(res.recognised, res.unrecognised),
          Number(percentage(res.unrecognised, res.recognised)),
          percentage(res.unrecognised, res.recognised),
        ]);
      });
      setRecogData(result);
    }

    if (
      results?.recognition_count !== null &&
      results?.recognition_count !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition_count?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          res.recognised,
          res.recognised,
          res.unrecognised,
          res.unrecognised,
        ]);
      });
      setRecogDataC(result);
    }

    if (
      results?.recognition_count_radio !== null &&
      results?.recognition_count_radio !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition_count_radio?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          res.recognised,
          res.recognised,
          res.unrecognised,
          res.unrecognised,
        ]);
      });
      setRecogRadioC(result);
    }

    if (results?.artist_data !== null && results?.artist_data !== undefined) {
      let result = [];
      result.push(['Artist', 'Count', { role: 'annotation' }]);
      results?.artist_data?.map((res) => {
        result.push([res.artist, res.count, res.count]);
      });
      setArtistData(result);
    }

    if (
      results?.artist_data_radio !== null &&
      results?.artist_data_radio !== undefined
    ) {
      let result = [];
      result.push(['Artist', 'Count', { role: 'annotation' }]);
      results?.artist_data_radio?.map((res) => {
        result.push([res.artist, res.count, res.count]);
      });
      setArtistRadio(result);
    }

    if (
      results?.international_artist_data !== null &&
      results?.international_artist_data !== undefined
    ) {
      let result = [];
      result.push(['Artist', 'Count', { role: 'annotation' }]);
      results?.international_artist_data?.map((res) => {
        result.push([res.artist, res.count, res.count]);
      });
      setIntArtistData(result);
    }

    if (
      results?.national_artist_data !== null &&
      results?.national_artist_data !== undefined
    ) {
      let result = [];
      result.push(['Artist', 'Count', { role: 'annotation' }]);
      results?.national_artist_data?.map((res) => {
        result.push([res.artist, res.count, res.count]);
      });
      setNatArtistData(result);
    }

    if (results?.title_data !== null && results?.title_data !== undefined) {
      let result = [];
      result.push(['Title', 'Count', { role: 'annotation' }]);
      results?.title_data?.map((res) => {
        result.push([res.title, res.count, res.count]);
      });
      setSongData(result);
    }

    if (
      results?.title_data_radio !== null &&
      results?.title_data_radio !== undefined
    ) {
      let result = [];
      result.push(['Title', 'Count', { role: 'annotation' }]);
      results?.title_data_radio?.map((res) => {
        result.push([res.title, res.count, res.count]);
      });
      setSongRadio(result);
    }

    if (
      results?.recognition_duration !== null &&
      results?.recognition_duration !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition_duration?.map((res) => {
        if (res.broadcast_stream__name != null)
          result.push([
            res.broadcast_stream__name,
            res.recognised ? res.recognised : 0,
            res.recognised ? res.recognised : 0,
            res.unrecognised ? res.unrecognised : 0,
            res.unrecognised ? res.unrecognised : 0,
          ]);
      });
      setRecogDuration(result);
    }

    if (
      results?.recognition_duration_radio !== null &&
      results?.recognition_duration_radio !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition_duration_radio?.map((res) => {
        if (res.broadcast_stream__name != null)
          result.push([
            res.broadcast_stream__name,
            res.recognised ? res.recognised : 0,
            res.recognised ? res.recognised : 0,
            res.unrecognised ? res.unrecognised : 0,
            res.unrecognised ? res.unrecognised : 0,
          ]);
      });
      setRecogDurationRadio(result);
    }

    if (
      results?.recognition_radio !== null &&
      results?.recognition_radio !== undefined
    ) {
      let result = [];
      result.push([
        'Channel',
        'Recognised',
        { role: 'annotation' },
        'Unrecognised',
        { role: 'annotation' },
      ]);
      results?.recognition_radio?.map((res) => {
        result.push([
          res.broadcast_stream__name,
          Number(percentage(res.recognised, res.unrecognised)),
          percentage(res.recognised, res.unrecognised),
          Number(percentage(res.unrecognised, res.recognised)),
          percentage(res.unrecognised, res.recognised),
        ]);
      });
      setRecogRadio(result);
    }
  }, [results]);

  return (
    <>
      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2">
                  Top 10 Most played Artists
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={artistData}
                  options={withHeadroom(options, artistData)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2">
                  Top 10 Most played Artists - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={artistRadio}
                  options={withHeadroom(options, artistRadio)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2">
                  Top 10 Most played National Artists
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={natArtistData}
                  options={withHeadroom(options, natArtistData)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2">
                  Top 10 Most played International Artists
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={intArtistData}
                  options={withHeadroom(
                    { ...options, series: { 0: { color: '#e74c3c' } } },
                    intArtistData,
                  )}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Top 10 Most played Songs
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={songData}
                  options={withHeadroom(options, songData)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Top 10 Most played Songs - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={songRadio}
                  options={withHeadroom(options, songRadio)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Local vs International Playtime(%)
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={data}
                  options={withHeadroom(IntOptions, data)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Local vs International Count (Distinct Songs)
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={datac}
                  options={withHeadroom(IntOptions, datac)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Local vs International Count (Distinct Songs) - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={radioc}
                  options={withHeadroom(IntOptions, radioc)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogData}
                  options={withHeadroom(IntOptions, recogData)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogRadio}
                  options={withHeadroom(IntOptions, recogRadio)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised (count)
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogDataC}
                  options={withHeadroom(IntOptions, recogDataC)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised (count) - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogRadioC}
                  options={withHeadroom(IntOptions, recogRadioC)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised (Duration - Seconds)
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogDuration}
                  options={withHeadroom(IntOptions, recogDuration)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
        <div className="sm:col-span-4">
          <div className="box">
            {loading ? (
              <Loader upload_message={'loading'} full_height={true} />
            ) : (
              <div className="box-wrapper">
                <h3 className="font-semibold text-2xl profile-overview-title text-center pb-2 pt-5">
                  Recognised vs Unrecognised (Duration - Seconds) - Radio
                </h3>
                <ReportChart
                  chartType="ColumnChart"
                  width="100%"
                  height={`500px`}
                  data={recogDurationRadio}
                  options={withHeadroom(IntOptions, recogDurationRadio)}
                  className={'rounded'}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
